import { jsPDF } from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';
import { getBestIndices, computeWinsByColumn } from '../components/comparison/comparisonHelpers';

applyPlugin(jsPDF);

/**
 * Declarative row definitions — mirrors ComparisonTable.js exactly.
 * type: 'section' | 'metric' | 'editable' | 'badge'
 * highlight: 'max' | 'min' | null
 * source + yearIndex: for time-series lookups (equityData, cashFlowProjection)
 */
const ROW_DEFS = [
    { type: 'section', label: 'DEAL RATING' },
    { type: 'metric', label: 'Cash on Cash ROI', key: 'cashOnCashROI', format: 'percent', decimals: 1, highlight: 'max' },
    { type: 'badge', label: 'Deal Rating', key: 'dealRating', colorKey: 'dealRatingColor' },

    { type: 'section', label: 'PURCHASE DETAILS' },
    { type: 'editable', label: 'Offer Price', field: 'purchasePrice', format: 'currency' },
    { type: 'editable', label: 'Improvements', field: 'improvements', format: 'currency' },
    { type: 'editable', label: 'Closing Costs', field: 'closingCosts', format: 'currency' },

    { type: 'section', label: 'REVENUE' },
    { type: 'editable', label: 'Rent /mo', field: 'rentPerUnit', format: 'currency' },
    { type: 'editable', label: 'Units', field: 'numberOfUnits', format: 'number' },

    { type: 'section', label: 'KEY METRICS' },
    { type: 'metric', label: 'Monthly Cash Flow', key: 'monthlyCashFlow', format: 'currency', highlight: 'max' },
    { type: 'metric', label: 'Monthly Mortgage', key: 'monthlyMortgage', format: 'currency', highlight: 'min' },
    { type: 'metric', label: 'NOI (Annual)', key: 'netOperatingIncome', format: 'currency', highlight: 'max' },
    { type: 'metric', label: 'Cap Rate', key: 'capRate', format: 'percent', decimals: 1, highlight: 'max' },
    { type: 'metric', label: 'Total ROI', key: 'totalROI', format: 'percent', decimals: 1, highlight: 'max' },
    { type: 'metric', label: 'Gross Rental Income', key: 'grossRentalIncome', format: 'currency', highlight: 'max' },
    { type: 'metric', label: 'Operating Expenses', key: 'totalOperatingExpenses', format: 'currency', highlight: 'min' },

    { type: 'section', label: 'CASH REQUIRED' },
    { type: 'metric', label: 'Cash to Close', key: 'cashInvested', format: 'currency', highlight: 'min' },
    { type: 'metric', label: 'Down Payment', key: 'downPaymentAmount', format: 'currency' },
    { type: 'metric', label: 'Loan Amount', key: 'loanAmount', format: 'currency' },

    { type: 'section', label: '10-YEAR PROJECTION' },
    { type: 'metric', label: 'Year 5 Equity', key: 'equity', source: 'equityData', yearIndex: 4, format: 'currency', highlight: 'max' },
    { type: 'metric', label: 'Year 10 Equity', key: 'equity', source: 'equityData', yearIndex: 9, format: 'currency', highlight: 'max' },
    { type: 'metric', label: 'Year 5 Cash Flow', key: 'cashFlow', source: 'cashFlowProjection', yearIndex: 4, format: 'currency', highlight: 'max' },
    { type: 'metric', label: 'Year 10 Cash Flow', key: 'cashFlow', source: 'cashFlowProjection', yearIndex: 9, format: 'currency', highlight: 'max' },
];

// --- Formatters ---

function fmtCurrency(v) {
    if (v == null || !Number.isFinite(v)) return '$0';
    const sign = v < 0 ? '-' : '';
    return sign + '$' + Math.abs(Math.round(v)).toLocaleString('en-US');
}

function fmtPercent(v, decimals = 1) {
    if (v == null || !Number.isFinite(v)) return '0.0%';
    return v.toFixed(decimals) + '%';
}

function fmtNumber(v) {
    if (v == null) return '0';
    return String(v);
}

function formatValue(value, format, decimals) {
    if (format === 'currency') return fmtCurrency(value);
    if (format === 'percent') return fmtPercent(value, decimals);
    return fmtNumber(value);
}

// --- Value extraction ---

function getMetricValue(results, columnId, row) {
    const r = results[columnId];
    if (!r) return null;
    if (row.source) {
        const arr = r[row.source];
        return arr?.[row.yearIndex]?.[row.key] ?? null;
    }
    return r[row.key] ?? null;
}

function getEditableValue(perColumnAssumptions, columnId, field) {
    return perColumnAssumptions[columnId]?.[field] ?? null;
}

// --- Colors ---

const BRAND_BLUE = [37, 99, 235];    // #2563eb
const GRAY_900 = [17, 24, 39];
const GRAY_600 = [75, 85, 99];
const GRAY_100 = [243, 244, 246];
const GRAY_200 = [229, 231, 235];
const GREEN_BG = [236, 253, 245];     // #ecfdf5
const GREEN_TEXT = [5, 150, 105];      // #059669
const RED_TEXT = [239, 68, 68];        // #ef4444
const WHITE = [255, 255, 255];
const STAR_GOLD = [245, 158, 11];      // #f59e0b — matches on-screen .ct-best-star
const AMBER_BG = [254, 243, 199];      // #fef3c7 — matches on-screen .ct-col-wins
const AMBER_TEXT = [146, 64, 14];      // #92400e — matches on-screen .ct-col-wins

function hexToRgb(hex) {
    const h = hex.replace('#', '');
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

// Draw a filled 5-point gold star as a polygon via doc.lines().
// jsPDF's default Helvetica is WinAnsi and cannot render Unicode '★', so we draw the shape directly.
function drawStar(doc, cx, cy, radius) {
    const innerR = radius * 0.382; // golden-ratio inner radius for classic 5-point star
    const points = [];
    for (let i = 0; i < 10; i++) {
        const angle = -Math.PI / 2 + (i * Math.PI) / 5;
        const r = i % 2 === 0 ? radius : innerR;
        points.push([cx + r * Math.cos(angle), cy + r * Math.sin(angle)]);
    }
    // doc.lines() takes an array of [dx, dy] deltas from the starting point.
    const deltas = [];
    for (let i = 1; i < points.length; i++) {
        deltas.push([points[i][0] - points[i - 1][0], points[i][1] - points[i - 1][1]]);
    }
    doc.setFillColor(...STAR_GOLD);
    doc.setDrawColor(...STAR_GOLD);
    doc.lines(deltas, points[0][0], points[0][1], [1, 1], 'F', true);
}

/**
 * Export comparison data as a PDF.
 */
export function exportComparisonPdf(columns, propertiesMap, perColumnAssumptions, results, globalOverrides) {
    const colCount = columns.length;
    const columnIds = columns.map(c => c.columnId);
    const winsByColumn = computeWinsByColumn(results, columnIds);
    const topWins = Math.max(0, ...Object.values(winsByColumn));
    const topPickColumns = new Set(
        colCount > 1 && topWins > 0
            ? columns
                .map((col, i) => (winsByColumn[col.columnId] === topWins ? `col_${i}` : null))
                .filter(Boolean)
            : []
    );

    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 30;

    // Dynamic font size
    const fontSize = colCount <= 4 ? 9 : colCount <= 6 ? 8 : 7;

    // --- Header ---
    doc.setFontSize(16);
    doc.setTextColor(...BRAND_BLUE);
    doc.text('Revest AI', margin, 36);

    doc.setFontSize(11);
    doc.setTextColor(...GRAY_600);
    doc.text('Property Comparison Report', margin + 80, 36);

    const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    doc.setFontSize(9);
    doc.setTextColor(...GRAY_600);
    doc.text(today, pageW - margin, 36, { align: 'right' });

    // Blue rule
    doc.setDrawColor(...BRAND_BLUE);
    doc.setLineWidth(1.5);
    doc.line(margin, 44, pageW - margin, 44);

    // --- Global Assumptions Strip ---
    const globals = globalOverrides || {};
    const globalText = [
        `Interest Rate: ${globals.interestRate ?? 6.89}%`,
        `Down Payment: ${globals.downPaymentPercent ?? 20}%`,
        `Vacancy: ${globals.vacancyRate ?? 5}%`,
        `Appreciation: ${globals.appreciationRate ?? 3}%/yr`,
        `DIY: ${globals.isDIY ? 'Yes' : 'No'}`,
    ].join('   |   ');

    doc.setFontSize(8);
    doc.setTextColor(...GRAY_600);
    doc.text(globalText, margin, 58);

    // --- Build Table Data ---
    const usableW = pageW - margin * 2;
    const labelColW = Math.min(120, usableW * 0.2);
    const dataColW = (usableW - labelColW) / colCount;

    // Column definitions for autotable
    const tableColumns = [
        { header: '', dataKey: 'label' },
        ...columns.map((col, i) => {
            const p = propertiesMap[col.propertyId];
            const addr = p ? (p.address || 'Unknown') : 'Unknown';
            return { header: addr, dataKey: `col_${i}` };
        }),
    ];

    // Property info rows (before metrics)
    const propertyInfoRows = [];

    // City/State row
    const cityStateRow = { label: 'Location' };
    columns.forEach((col, i) => {
        const p = propertiesMap[col.propertyId];
        cityStateRow[`col_${i}`] = p ? `${p.city || ''}, ${p.state || ''}` : '';
    });
    propertyInfoRows.push(cityStateRow);

    // Specs row
    const specsRow = { label: 'Specs' };
    columns.forEach((col, i) => {
        const p = propertiesMap[col.propertyId];
        if (p) {
            const parts = [];
            if (p.beds) parts.push(`${p.beds} bed`);
            if (p.baths) parts.push(`${p.baths} bath`);
            if (p.sqft) parts.push(`${p.sqft.toLocaleString()} sqft`);
            specsRow[`col_${i}`] = parts.join(' | ');
        } else {
            specsRow[`col_${i}`] = '';
        }
    });
    propertyInfoRows.push(specsRow);

    // List Price row
    const priceRow = { label: 'List Price' };
    columns.forEach((col, i) => {
        const p = propertiesMap[col.propertyId];
        priceRow[`col_${i}`] = p ? fmtCurrency(p.list_price) : '';
    });
    propertyInfoRows.push(priceRow);

    // Scenario name row (only if any column has a scenario)
    const hasScenarios = columns.some(c => c.scenarioName);
    if (hasScenarios) {
        const scenarioRow = { label: 'Scenario' };
        columns.forEach((col, i) => {
            scenarioRow[`col_${i}`] = col.scenarioName || 'Default';
        });
        propertyInfoRows.push(scenarioRow);
    }

    // Wins tally row — mirrors the on-screen "N wins / Top Pick" chip
    const winsRow = { label: 'Wins' };
    columns.forEach((col, i) => {
        const count = winsByColumn[col.columnId] || 0;
        const isTopPick = topPickColumns.has(`col_${i}`);
        winsRow[`col_${i}`] = isTopPick ? `${count} wins  ·  Top Pick` : `${count} wins`;
    });
    propertyInfoRows.push(winsRow);

    // Build body rows from ROW_DEFS
    const bodyRows = [];
    // Track which body row indices are section headers, best cells, badges, negatives
    const sectionRowIndices = new Set();
    const bestCells = new Set();           // "rowIdx_colDataKey" — supports multiple winners per row (ties)
    const badgeCells = {};                 // rowIdx -> { colDataKey: color }
    const negativeCells = new Set();       // "rowIdx_colDataKey"
    let winsRowIdx = -1;                   // body index of the "Wins" row (for amber Top Pick styling)

    // Property info section
    const propSectionIdx = bodyRows.length;
    bodyRows.push({ label: 'PROPERTY INFO' });
    sectionRowIndices.add(propSectionIdx);

    propertyInfoRows.forEach(r => {
        if (r === winsRow) winsRowIdx = bodyRows.length;
        bodyRows.push(r);
    });

    // Metric rows
    for (const row of ROW_DEFS) {
        const rowIdx = bodyRows.length;

        if (row.type === 'section') {
            bodyRows.push({ label: row.label });
            sectionRowIndices.add(rowIdx);
            continue;
        }

        const dataRow = { label: row.label };
        const rawValues = [];

        columns.forEach((col, i) => {
            const colKey = `col_${i}`;
            let rawVal, displayVal;

            if (row.type === 'editable') {
                rawVal = getEditableValue(perColumnAssumptions, col.columnId, row.field);
                displayVal = formatValue(rawVal, row.format, row.decimals);
            } else if (row.type === 'badge') {
                rawVal = getMetricValue(results, col.columnId, row);
                displayVal = rawVal || '';
                // Store badge color
                const color = results[col.columnId]?.dealRatingColor;
                if (color) {
                    if (!badgeCells[rowIdx]) badgeCells[rowIdx] = {};
                    badgeCells[rowIdx][colKey] = color;
                }
            } else {
                rawVal = getMetricValue(results, col.columnId, row);
                displayVal = formatValue(rawVal, row.format, row.decimals);
                // Track negatives for currency values
                if (row.format === 'currency' && Number.isFinite(rawVal) && rawVal < 0) {
                    negativeCells.add(`${rowIdx}_${colKey}`);
                }
            }

            dataRow[colKey] = displayVal;
            rawValues.push(Number.isFinite(rawVal) ? rawVal : null);
        });

        // Best-value detection — uses the shared helper so PDF matches the live table's tie handling
        if (row.highlight) {
            const indices = getBestIndices(rawValues, row.highlight);
            indices.forEach(idx => bestCells.add(`${rowIdx}_col_${idx}`));
        }

        bodyRows.push(dataRow);
    }

    // --- Render Table ---
    doc.autoTable({
        startY: 66,
        margin: { left: margin, right: margin, bottom: 40 },
        head: [tableColumns.map(c => c.header)],
        body: bodyRows.map(r => tableColumns.map(c => r[c.dataKey] || '')),
        theme: 'grid',
        styles: {
            fontSize,
            cellPadding: { top: 4, bottom: 4, left: 6, right: 6 },
            lineColor: GRAY_200,
            lineWidth: 0.5,
            textColor: GRAY_900,
            font: 'helvetica',
            overflow: 'linebreak',
        },
        headStyles: {
            fillColor: GRAY_900,
            textColor: WHITE,
            fontStyle: 'bold',
            fontSize: Math.max(fontSize, 7),
            halign: 'center',
            cellPadding: { top: 6, bottom: 6, left: 4, right: 4 },
        },
        columnStyles: {
            0: {
                cellWidth: labelColW,
                fontStyle: 'bold',
                textColor: GRAY_600,
            },
            ...Object.fromEntries(
                columns.map((_, i) => [i + 1, { cellWidth: dataColW, halign: 'center' }])
            ),
        },
        didParseCell: (data) => {
            if (data.section !== 'body') return;
            const rowIdx = data.row.index;
            const colIdx = data.column.index;
            const colKey = `col_${colIdx - 1}`;

            // Section header rows
            if (sectionRowIndices.has(rowIdx)) {
                data.cell.styles.fillColor = GRAY_100;
                data.cell.styles.fontStyle = 'bold';
                data.cell.styles.textColor = GRAY_600;
                data.cell.styles.fontSize = fontSize - 1;
                if (colIdx === 0) {
                    data.cell.colSpan = colCount + 1;
                }
            }

            // Best-value highlighting (may apply to multiple columns on ties)
            if (colIdx > 0 && bestCells.has(`${rowIdx}_${colKey}`)) {
                data.cell.styles.fillColor = GREEN_BG;
                data.cell.styles.textColor = GREEN_TEXT;
                data.cell.styles.fontStyle = 'bold';
            }

            // Wins-row Top Pick amber highlighting
            if (colIdx > 0 && rowIdx === winsRowIdx && topPickColumns.has(colKey)) {
                data.cell.styles.fillColor = AMBER_BG;
                data.cell.styles.textColor = AMBER_TEXT;
                data.cell.styles.fontStyle = 'bold';
            }

            // Badge coloring (deal rating)
            if (colIdx > 0 && badgeCells[rowIdx]?.[colKey]) {
                data.cell.styles.textColor = hexToRgb(badgeCells[rowIdx][colKey]);
                data.cell.styles.fontStyle = 'bold';
            }

            // Negative values in red
            if (colIdx > 0 && negativeCells.has(`${rowIdx}_${colKey}`)) {
                data.cell.styles.textColor = RED_TEXT;
            }
        },
        didDrawCell: (data) => {
            if (data.section !== 'body') return;
            const colIdx = data.column.index;
            if (colIdx === 0) return;
            const rowIdx = data.row.index;
            const colKey = `col_${colIdx - 1}`;
            if (bestCells.has(`${rowIdx}_${colKey}`)) {
                drawStar(doc, data.cell.x + 5, data.cell.y + data.cell.height / 2, 3);
            }
        },
        didDrawPage: (data) => {
            // Footer on every page
            const pageNum = doc.internal.getNumberOfPages();
            doc.setFontSize(8);
            doc.setTextColor(...GRAY_600);
            doc.text('Generated by Revest AI', margin, pageH - 20);
            doc.text(`Page ${data.pageNumber} of ${pageNum}`, pageW - margin, pageH - 20, { align: 'right' });
        },
    });

    // Fix page numbers (total pages known after rendering)
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        // Overwrite footer area
        doc.setFillColor(255, 255, 255);
        doc.rect(pageW - margin - 80, pageH - 28, 80, 16, 'F');
        doc.setFontSize(8);
        doc.setTextColor(...GRAY_600);
        doc.text(`Page ${i} of ${totalPages}`, pageW - margin, pageH - 20, { align: 'right' });
    }

    // Open in a new browser tab. Using 'bloburl' (not 'dataurlnewwindow') avoids base64
    // overhead and the browser's data-URL size limits. The user can still download from
    // the viewer's built-in save button if they want a local copy.
    const blobUrl = doc.output('bloburl');
    window.open(blobUrl, '_blank');
}
